import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api, type Agent } from '../api';
import { Page, Card, Button, Field, Input, Textarea, Select, Modal, EmptyState, Badge, AgentAvatar } from '../ui';

export function AgentsList() {
  const { slug } = useParams<{ slug: string }>();
  const nav = useNavigate();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // form de criação
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scope, setScope] = useState<'docs' | 'code'>('docs');
  const [engine, setEngine] = useState<'claude' | 'hermes'>('claude');
  const [hermesOn, setHermesOn] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    api
      .listAgents(slug)
      .then(({ agents }) => setAgents(agents))
      .catch(() => setErr('Falha ao carregar agentes'))
      .finally(() => setLoading(false));
    api.authProviders().then((p) => setHermesOn(p.hermes)).catch(() => undefined);
  }, [slug]);

  function slugify(s: string): string {
    return s
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 64);
  }

  async function create() {
    if (!slug || !name.trim() || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const agentSlug = slugify(name);
      const { agent } = await api.createAgent(slug, { slug: agentSlug, name: name.trim(), description: description.trim(), scope, engine });
      setCreating(false);
      setName('');
      setDescription('');
      setScope('docs');
      setEngine('claude');
      // Vai direto pro editor, onde pode pedir ajuda à IA pra gerar o soul.
      nav(`/p/${slug}/agents/${agent.id}`);
    } catch (e) {
      const code = (e as { error?: string }).error;
      setErr(code === 'slug_taken' ? 'Já existe um agente com esse nome/slug.' : 'Falha ao criar agente');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Page
      title="Agentes"
      subtitle="Personas de conhecimento do projeto. Cada uma tem instruções próprias e cria/edita documentos."
      actions={
        <Button variant="primary" onClick={() => setCreating(true)}>
          + Novo agente
        </Button>
      }
    >
      {err && <div className="chat-error">{err}</div>}
      {loading ? (
        <div className="center">Carregando…</div>
      ) : agents.length === 0 ? (
        <EmptyState
          title="Nenhum agente ainda"
          description="Crie o primeiro agente e peça ajuda à IA pra escrever as instruções dele."
          action={
            <Button variant="primary" onClick={() => setCreating(true)}>
              + Novo agente
            </Button>
          }
        />
      ) : (
        <div className="agent-grid">
          {agents.map((a) => (
            <Link key={a.id} to={`/p/${slug}/agents/${a.id}`} className="agent-card-link">
              <Card className="agent-card">
                <div className="agent-card-head">
                  <AgentAvatar slug={slug!} agent={a} />
                  <div className="agent-card-id">
                    <div className="agent-card-name">
                      {a.name}
                      {a.isRouter && <Badge variant="accent">recepcionista</Badge>}
                      {a.pipelineRole && <Badge variant="warn">{a.pipelineRole}</Badge>}
                      {a.scope === 'code' && !a.pipelineRole && <Badge variant="ok">código</Badge>}
                      {!a.enabled && <Badge variant="neutral">desativado</Badge>}
                    </div>
                    <div className="agent-card-slug">{a.slug}</div>
                  </div>
                </div>
                <div className="agent-card-desc">{a.description || <em>sem descrição</em>}</div>
              </Card>
            </Link>
          ))}
        </div>
      )}

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="Novo agente"
        description="Dê um nome e descreva o que ele faz. No próximo passo a IA pode gerar as instruções."
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreating(false)}>
              cancelar
            </Button>
            <Button variant="primary" onClick={create} disabled={!name.trim() || busy}>
              {busy ? 'criando…' : 'criar'}
            </Button>
          </>
        }
      >
        <Field label="Nome">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex.: Posicionamento" autoFocus />
        </Field>
        <Field label="O que ele faz" hint="Uma frase. Alimenta o recepcionista e a geração do soul.">
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder="Ex.: Define público-alvo, proposta de valor e mensagens-chave."
          />
        </Field>
        <Field label="Tipo" hint="Conhecimento escreve documentos; Código analisa o repositório e abre PRs.">
          <Select value={scope} onChange={(e) => setScope(e.target.value as 'docs' | 'code')}>
            <option value="docs">Conhecimento (docs)</option>
            <option value="code">Código (analisa repo → PR)</option>
          </Select>
        </Field>
        {hermesOn && (
          <Field label="Motor" hint="Hermes: marketing/conteúdo. Claude Code: tarefas de código.">
            <Select value={engine} onChange={(e) => setEngine(e.target.value as 'claude' | 'hermes')}>
              <option value="hermes">Hermes (conteúdo / marketing)</option>
              <option value="claude">Claude Code (código)</option>
            </Select>
          </Field>
        )}
      </Modal>
    </Page>
  );
}

